import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChevronDown, Info, Play, Plus, X } from '../bravosIcons';
import { WorkoutService } from '../../../lib/workoutService';
import { getWorkoutDisplayBannerUrl } from '../../../lib/workoutBannerImage';
import { getYouTubeEmbedUrl, getYouTubeThumbnail, isYouTubeUrl } from '../../../utils/youtubeUtils';
import { TrainerAppLayout } from '../shell/TrainerAppLayout';
import { createTrainerDrillDownSectionSelect, persistTrainerDashboardSection } from '../shell/trainerSectionNav';
import { ExerciseDetailDrawer } from './overlays/ExerciseDetailDrawer';
import { ExerciseEditDrawer, type ExerciseEditDrawerHandle } from './overlays/ExerciseEditDrawer';
import { ExerciseEditSavedToast } from './overlays/ExerciseEditSavedToast';
import { ExerciseService } from '../../../lib/exerciseService';
import { TrainerMemberService } from '../../../lib/trainerMemberService';
import { useAuth } from '../../../contexts/AuthContext';
import { displayNameFromProfile } from '../../../pages/account/accountShared';
import { useAssignMembersCache } from '../dashboard/hooks/useAssignMembersCache';
import { useAssignWorkoutModal } from '../dashboard/hooks/useAssignWorkoutModal';
import { AssignWorkoutModal } from '../dashboard/overlays/AssignWorkoutModal';
import { TrainerActionButton } from '../components/TrainerActionButton';
import { TrainerModeToggle } from '../components/TrainerModeToggle';
import type {
  CreateExerciseData,
  Exercise,
  ExerciseSet,
  WorkoutBlock,
  WorkoutWithBlocks,
} from '../../../types/dataModel';
import { TrainerWorkoutDetailEditView } from './TrainerWorkoutDetailEditView';
import { TrainerConfirmDialog } from './overlays/TrainerConfirmDialog';
import './TrainerWorkoutDetailPage.css';
import './TrainerWorkoutDetailPage.edit.css';
import '../dashboard/overlays/AssignWorkoutPlanModal.css';

/** Middle dot (U+00B7) for inline metadata. */
const META_SEP = '\u00B7';

type BlockWithSets = WorkoutBlock & {
  exerciseSets: (ExerciseSet & { exercise: import('../../../types/dataModel').Exercise })[];
};

/** Run = consecutive ExerciseSet rows that belong to the same exercise instance.
 *  Mirrors `WorkoutDetailsPage` / `TrainerWorkoutDetailModal` grouping rules. */
type ExerciseRun = {
  key: string;
  exercise: import('../../../types/dataModel').Exercise;
  sets: (ExerciseSet & { exercise: import('../../../types/dataModel').Exercise })[];
};

function exerciseRunKey(s: ExerciseSet): string {
  if (s.exerciseGroupId != null && String(s.exerciseGroupId).trim() !== '') {
    return `group:${s.exerciseGroupId}`;
  }
  if (s.exerciseId && String(s.exerciseId).trim() !== '') {
    return String(s.exerciseId);
  }
  return '__no_exercise__';
}

function groupExerciseRuns(block: BlockWithSets): ExerciseRun[] {
  const sets = block.exerciseSets ?? [];
  if (!sets.length) return [];
  const runs: ExerciseRun[] = [];
  let cur: ExerciseRun | null = null;
  for (const s of sets) {
    const key = exerciseRunKey(s);
    if (!cur || cur.key !== key) {
      cur = { key, exercise: s.exercise, sets: [s] };
      runs.push(cur);
    } else {
      cur.sets.push(s);
    }
  }
  return runs;
}

/** All sets share the same `rep`/`repText`, `weightKg`, and `restSeconds`. */
function areSetsUniform(sets: ExerciseSet[]): boolean {
  if (sets.length <= 1) return true;
  const repKey = (s: ExerciseSet) => {
    if (s.repText != null && String(s.repText).trim() !== '') return `t:${s.repText}`;
    if (s.rep != null) return `n:${s.rep}`;
    return '';
  };
  const r0 = repKey(sets[0]);
  const w0 = sets[0].weightKg ?? null;
  const rest0 = sets[0].restSeconds ?? null;
  return sets.every(
    (s) =>
      repKey(s) === r0 &&
      (s.weightKg ?? null) === w0 &&
      (s.restSeconds ?? null) === rest0,
  );
}

function formatBlockType(type?: string): string {
  if (!type) return '';
  if (type === 'Default') return 'Regular';
  return String(type).replace(/_/g, ' ');
}

function isRegularBlock(type?: string): boolean {
  return !type || type === 'Default';
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

type StatValue = { text: string; muted: boolean };

/** Treat both NULL and 0 as "not specified" for display — mirrors the HTML mock's
 *  Barbell Pullover example (muted `—:—` / `—` regardless of how the DB stores absence). */
function formatRest(seconds: number | null | undefined): StatValue {
  if (seconds == null || Number(seconds) <= 0) return { text: '\u2014:\u2014', muted: true };
  const safe = Math.max(0, Math.floor(Number(seconds) || 0));
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return { text: `${m}:${pad2(s)}`, muted: false };
}

/** Reps win when both are present (should not happen by schema, but defensive). */
function isDurationBasedSet(s: ExerciseSet): boolean {
  if (s.repText != null && String(s.repText).trim() !== '') return false;
  if (s.rep != null) return false;
  return s.durationSeconds != null && Number(s.durationSeconds) > 0;
}

function formatReps(s: ExerciseSet): StatValue {
  if (s.repText != null && String(s.repText).trim() !== '') return { text: String(s.repText), muted: false };
  if (s.rep != null) return { text: String(s.rep), muted: false };
  return { text: '\u2014', muted: true };
}

/** Same M:SS shape as `formatRest` (e.g. `0:40`). */
function formatDuration(seconds: number | null | undefined): StatValue {
  if (seconds == null || Number(seconds) <= 0) return { text: '\u2014', muted: true };
  const safe = Math.max(0, Math.floor(Number(seconds) || 0));
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return { text: `${m}:${pad2(s)}`, muted: false };
}

function formatWeight(s: ExerciseSet): StatValue {
  if (s.weightKg != null && Number(s.weightKg) > 0) return { text: String(s.weightKg), muted: false };
  return { text: '\u2014', muted: true };
}

function capitalizeFirst(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Equipment / muscle-group union, deduped (case-insensitive trim), sorted alpha. */
function unionFromExercises(
  blocks: BlockWithSets[],
  picker: (e: import('../../../types/dataModel').Exercise) => string | null | undefined,
): string[] {
  const seen = new Map<string, string>();
  for (const b of blocks ?? []) {
    for (const es of b.exerciseSets ?? []) {
      const v = picker(es.exercise);
      if (!v) continue;
      const trimmed = String(v).trim();
      if (!trimmed) continue;
      const k = trimmed.toLowerCase();
      if (!seen.has(k)) seen.set(k, trimmed);
    }
  }
  return Array.from(seen.values()).sort((a, b) => a.localeCompare(b));
}

/** Total distinct exercises across all blocks (matches dashboard count semantics). */
function countDistinctExercises(blocks: BlockWithSets[]): number {
  const ids = new Set<string>();
  for (const b of blocks ?? []) {
    for (const es of b.exerciseSets ?? []) {
      if (es.exerciseId) ids.add(String(es.exerciseId));
    }
  }
  return ids.size;
}

interface BlockHeaderProps {
  index: number;
  block: BlockWithSets;
  open: boolean;
  onToggle: () => void;
}

const BlockHeader: React.FC<BlockHeaderProps> = ({ index, block, open, onToggle }) => {
  const typeLabel = formatBlockType(block.type);
  const showVolumePill = !isRegularBlock(block.type);
  /** AMRAP stores its minute duration in `block.sets` (precedent: TrainerWorkoutDetailModal). */
  const isAmrap = String(block.type || '').toLowerCase() === 'amrap';
  const volumeText = isAmrap
    ? `${Math.max(0, Number(block.sets) || 0)} min`
    : `${Number(block.sets) || 0} sets`;

  return (
    <button
      type="button"
      className="twd-block-bar"
      aria-expanded={open}
      onClick={onToggle}
    >
      <span className="twd-block-index tnum">{pad2(index + 1)}</span>
      <div className="twd-block-meta">
        <span className="twd-block-name">
          {block.name && block.name.trim() !== '' ? block.name : `Block ${index + 1}`}
        </span>
        {(typeLabel || showVolumePill) && (
          <div className="twd-block-tags">
            {typeLabel && <span className="twd-pill twd-pill-type">{typeLabel}</span>}
            {showVolumePill && (
              <span className="twd-pill twd-pill-volume tnum">{volumeText}</span>
            )}
          </div>
        )}
      </div>
      <ChevronDown className="twd-block-chevron" aria-hidden="true" />
    </button>
  );
};

interface SetRowProps {
  label: string;
  set: ExerciseSet;
  allSets?: boolean;
}

const SetRow: React.FC<SetRowProps> = ({ label, set, allSets }) => {
  const durationBased = isDurationBasedSet(set);
  const measure = durationBased ? formatDuration(set.durationSeconds) : formatReps(set);
  const weight = formatWeight(set);
  const rest = formatRest(set.restSeconds);
  return (
    <div className={`twd-sets-row ${allSets ? 'twd-sets-row-all' : ''}`} role="row">
      <span className="twd-set-label" role="cell">{label}</span>
      <div className="twd-stat" role="cell">
        <span className="twd-stat-label">{durationBased ? 'Duration' : 'Reps'}</span>
        <span className={`twd-stat-val tnum ${measure.muted ? 'twd-stat-muted' : ''}`}>{measure.text}</span>
      </div>
      <div className="twd-stat twd-stat-weight" role="cell">
        <span className="twd-stat-label">Weight</span>
        <span className="twd-stat-weight-nums">
          <span className={`twd-stat-val tnum ${weight.muted ? 'twd-stat-muted' : ''}`}>{weight.text}</span>
          <span className="twd-stat-unit">kg</span>
        </span>
      </div>
      <div className="twd-stat" role="cell">
        <span className="twd-stat-label">Rest</span>
        <span className={`twd-stat-val tnum ${rest.muted ? 'twd-stat-muted' : ''}`}>{rest.text}</span>
      </div>
    </div>
  );
};

interface ExerciseRowProps {
  run: ExerciseRun;
  onOpenDetail: (exercise: Exercise, triggerRef: React.RefObject<HTMLButtonElement | null>) => void;
}

const ExerciseRow: React.FC<ExerciseRowProps> = ({ run, onOpenDetail }) => {
  const detailsBtnRef = useRef<HTMLButtonElement>(null);
  const videoModalTitleId = useId();
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const uniform = areSetsUniform(run.sets);
  const setCount = run.sets.length;
  const variation = uniform ? 'Same set variation' : 'Vary by set';
  const videoUrl = run.exercise?.video;
  const hasVideo = !!videoUrl && String(videoUrl).trim() !== '';
  const ytThumb = hasVideo ? getYouTubeThumbnail(videoUrl) : null;
  const isYouTube = hasVideo && isYouTubeUrl(videoUrl);
  const embedAutoplay = isYouTube ? getYouTubeEmbedUrl(videoUrl, { autoplay: true }) : null;
  const uniformRowLabel = setCount === 1 ? 'Set 01' : 'All sets';

  useEffect(() => {
    if (!videoModalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setVideoModalOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [videoModalOpen]);

  const handleVideoActivate = () => {
    if (!hasVideo || !videoUrl) return;
    setVideoModalOpen(true);
  };

  const videoModal =
    videoModalOpen && hasVideo && videoUrl
      ? createPortal(
          <div
            className="twd-video-modal-root"
            role="dialog"
            aria-modal="true"
            aria-labelledby={videoModalTitleId}
            onClick={() => setVideoModalOpen(false)}
          >
            <div
              className="twd-video-modal-panel"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="twd-video-modal-head">
                <h2 id={videoModalTitleId} className="twd-video-modal-title">
                  {run.exercise?.name || 'Exercise video'}
                </h2>
                <button
                  type="button"
                  className="twd-video-modal-close"
                  onClick={() => setVideoModalOpen(false)}
                  aria-label="Close"
                >
                  <X className="twd-video-modal-close-icon" strokeWidth={2} aria-hidden />
                </button>
              </div>
              <div className="twd-video-modal-frame">
                {isYouTube && embedAutoplay ? (
                  <iframe
                    src={embedAutoplay}
                    title={run.exercise?.name ? `Video: ${run.exercise.name}` : 'Exercise video'}
                    className="twd-video-modal-iframe"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                    allowFullScreen
                    referrerPolicy="strict-origin-when-cross-origin"
                  />
                ) : (
                  <video
                    src={videoUrl}
                    className="twd-video-modal-native"
                    controls
                    autoPlay
                    playsInline
                  >
                    Your browser does not support the video tag.
                  </video>
                )}
              </div>
              {isYouTube ? (
                <p className="twd-video-modal-hint">
                  Plays muted at first — use the player controls for sound.
                </p>
              ) : null}
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <>
    <div className="twd-exercise">
      <div className="twd-exercise-info">
        <div className="twd-exercise-header">
          <h4 className="twd-exercise-name">{run.exercise?.name || 'Untitled exercise'}</h4>
          <button
            ref={detailsBtnRef}
            type="button"
            className="twd-details-btn"
            aria-haspopup="dialog"
            onClick={() => onOpenDetail(run.exercise, detailsBtnRef)}
          >
            <Info className="twd-details-btn-icon" aria-hidden="true" />
            Details
          </button>
        </div>
        <p className="twd-exercise-meta">
          <strong>
            {setCount} {setCount === 1 ? 'set' : 'sets'}
          </strong>
          <span className="twd-meta-sep" aria-hidden="true">{META_SEP}</span>
          <span>{variation}</span>
        </p>
        <div className="twd-sets-table" role="table" aria-label={`Sets for ${run.exercise?.name || 'exercise'}`}>
          {uniform ? (
            <SetRow label={uniformRowLabel} set={run.sets[0]} allSets />
          ) : (
            run.sets.map((s, i) => (
              <SetRow
                key={s.id || `${run.key}-${i}`}
                label={`Set ${pad2(i + 1)}`}
                set={s}
              />
            ))
          )}
        </div>
      </div>
      <div className="twd-video-slot">
        {hasVideo ? (
          <button
            type="button"
            className="twd-video-tile twd-video-has"
            onClick={handleVideoActivate}
            aria-label={`Play video: ${run.exercise?.name || 'exercise'}`}
            aria-haspopup="dialog"
          >
            {ytThumb && (
              <img
                className="twd-video-thumb"
                src={ytThumb}
                alt=""
                loading="lazy"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
            )}
            <span className="twd-play-btn" aria-hidden="true">
              <Play fill="currentColor" />
            </span>
          </button>
        ) : (
          <div className="twd-video-tile twd-video-empty" aria-hidden="true" />
        )}
      </div>
    </div>
    {videoModal}
    </>
  );
};

const SkeletonState: React.FC = () => (
  <>
    <div className="twd-skeleton twd-skel-hero" aria-hidden="true" />
    <div className="twd-skeleton twd-skel-block" aria-hidden="true" />
    <div className="twd-skeleton twd-skel-block" aria-hidden="true" />
  </>
);

const NotFoundState: React.FC<{ backLabel: string; onBack: () => void }> = ({ backLabel, onBack }) => (
  <div className="twd-empty">
    <h2>Workout not found.</h2>
    <p>This workout may have been deleted or you may not have access to it.</p>
    <a
      href="/trainer-dashboard"
      onClick={(e) => {
        e.preventDefault();
        onBack();
      }}
    >
      {backLabel}
    </a>
  </div>
);

type WorkoutDetailOrigin = 'library' | 'member' | 'plan';

function deriveWorkoutDetailRoute(params: {
  id?: string;
  memberId?: string;
  workoutId?: string;
  planId?: string;
}): {
  origin: WorkoutDetailOrigin;
  workoutId: string;
  memberId?: string;
  planId?: string;
} {
  if (params.planId && params.workoutId) {
    return { origin: 'plan', workoutId: params.workoutId, planId: params.planId };
  }
  if (params.memberId && params.workoutId) {
    return { origin: 'member', workoutId: params.workoutId, memberId: params.memberId };
  }
  if (params.id) {
    return { origin: 'library', workoutId: params.id };
  }
  return { origin: 'library', workoutId: '' };
}

export const TrainerWorkoutDetailPage: React.FC = () => {
  const params = useParams<{ id?: string; memberId?: string; workoutId?: string; planId?: string }>();
  const { origin, workoutId, memberId, planId } = deriveWorkoutDetailRoute(params);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { profile } = useAuth();
  const libraryEditEnabled = origin === 'library';
  const initialMode =
    libraryEditEnabled && searchParams.get('mode') === 'edit' ? 'edit' : 'view';
  const [pageMode, setPageMode] = useState<'view' | 'edit'>(initialMode);
  const [editDirty, setEditDirty] = useState(false);
  const [workoutSaveToast, setWorkoutSaveToast] = useState(false);
  const navHints = (location.state ?? null) as {
    memberDisplayName?: string;
    planName?: string;
  } | null;
  const [duplicateInFlight, setDuplicateInFlight] = useState(false);
  const [duplicateToastOpen, setDuplicateToastOpen] = useState(false);
  const [workoutDeleteConfirm, setWorkoutDeleteConfirm] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [pendingViewAfterDiscard, setPendingViewAfterDiscard] = useState(false);

  const [workout, setWorkout] = useState<WorkoutWithBlocks | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [errored, setErrored] = useState<boolean>(false);
  const [memberDisplayName, setMemberDisplayName] = useState<string | null>(
    () => navHints?.memberDisplayName ?? null,
  );
  const [planTitle, setPlanTitle] = useState<string | null>(() => navHints?.planName ?? null);

  /** First block expanded, others collapsed (per spec §6.10). */
  const [openBlocks, setOpenBlocks] = useState<Set<string>>(new Set());

  /** Exercise detail drawer state. */
  const [drawerExercise, setDrawerExercise] = useState<Exercise | null>(null);
  const [drawerIsEdit, setDrawerIsEdit] = useState(false);
  const [tagOptions, setTagOptions] = useState<{ muscle: string[]; equipment: string[] }>({
    muscle: [],
    equipment: [],
  });
  const [savedToastExerciseName, setSavedToastExerciseName] = useState<string | null>(null);
  const drawerTriggerRef = useRef<HTMLButtonElement | null>(null);
  const exerciseEditRef = useRef<ExerciseEditDrawerHandle | null>(null);

  const assignMembersCache = useAssignMembersCache(profile?.id);
  const assignWorkout = useAssignWorkoutModal({
    workouts: workout ? [{ id: workout.id, name: workout.name }] : [],
    setOpenWorkoutMenu: () => {},
    onNavigateToAddMember: () => {
      persistTrainerDashboardSection('members');
      navigate('/trainer-dashboard');
    },
    assignMembersCache,
  });

  useEffect(() => {
    if (!libraryEditEnabled) {
      setPageMode('view');
      return;
    }
    setPageMode(searchParams.get('mode') === 'edit' ? 'edit' : 'view');
  }, [searchParams, libraryEditEnabled, workoutId]);

  useEffect(() => {
    if (!editDirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [editDirty]);

  useEffect(() => {
    setMemberDisplayName(
      origin === 'member' ? navHints?.memberDisplayName ?? null : null,
    );
    setPlanTitle(origin === 'plan' ? navHints?.planName ?? null : null);
  }, [origin, navHints?.memberDisplayName, navHints?.planName, location.key]);

  useEffect(() => {
    let cancelled = false;
    if (!workoutId) {
      setLoading(false);
      setErrored(true);
      return;
    }
    setLoading(true);
    setErrored(false);

    if (origin === 'member' && memberId && profile?.id) {
      TrainerMemberService.getTrainerMemberByMemberId(profile.id, memberId).then((rel) => {
        if (cancelled || !rel?.member) return;
        setMemberDisplayName(displayNameFromProfile(rel.member) || rel.member.email || 'Member');
      });
    }
    if (origin === 'plan' && planId) {
      WorkoutService.getWorkoutPlanById(planId).then((plan) => {
        if (cancelled || !plan) return;
        setPlanTitle(plan.name?.trim() ? plan.name : 'Plan');
      });
    }

    WorkoutService.getWorkoutById(workoutId)
      .then((w) => {
        if (cancelled) return;
        setWorkout(w);
        if (w?.workoutBlocks?.length) {
          setOpenBlocks(new Set([w.workoutBlocks[0].id]));
        }
      })
      .catch((err) => {
        console.error('Error loading workout:', err);
        if (cancelled) return;
        setErrored(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [workoutId, origin, memberId, planId, profile?.id]);

  /** Scroll lock + pointer-events suppression while the exercise detail or edit drawer is open. */
  useEffect(() => {
    if (!drawerExercise) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [drawerExercise]);

  useEffect(() => {
    if (!drawerIsEdit || !drawerExercise) return;
    let cancelled = false;
    ExerciseService.getDistinctExerciseTagValues()
      .then((opts) => {
        if (!cancelled) setTagOptions(opts);
      })
      .catch((err) => console.error('Distinct exercise tags:', err));
    return () => {
      cancelled = true;
    };
  }, [drawerIsEdit, drawerExercise]);

  const handleBackToLibrary = () => {
    try {
      localStorage.setItem('trainer_dashboard_active_section', 'library');
      localStorage.setItem('trainer_dashboard_active_tab', 'workouts');
    } catch {
      // ignore
    }
    navigate('/trainer-dashboard');
  };

  const handleBackToMemberDetail = () => {
    if (!memberId) {
      navigate('/trainer-dashboard');
      return;
    }
    navigate(`/trainer-dashboard/member/${memberId}`);
  };

  const handleBackToMemberManagement = () => {
    try {
      localStorage.setItem('trainer_dashboard_active_section', 'members');
    } catch {
      // ignore
    }
    navigate('/trainer-dashboard');
  };

  const handleBackToWorkoutPlansLibrary = () => {
    try {
      localStorage.setItem('trainer_dashboard_active_section', 'library');
      localStorage.setItem('trainer_dashboard_active_tab', 'workoutPlans');
    } catch {
      // ignore
    }
    navigate('/trainer-dashboard');
  };

  const handleBackToPlanDetail = () => {
    if (!planId) {
      navigate('/trainer-dashboard');
      return;
    }
    navigate(`/trainer-dashboard/workout-plan/${planId}`);
  };

  const notFoundBack =
    origin === 'member' && memberId
      ? { label: 'Back to member', onBack: handleBackToMemberDetail }
      : origin === 'plan' && planId
        ? { label: 'Back to plan', onBack: handleBackToPlanDetail }
        : { label: 'Back to Library', onBack: handleBackToLibrary };

  const blocks = (workout?.workoutBlocks ?? []) as BlockWithSets[];
  const equipment = useMemo(
    () => unionFromExercises(blocks, (e) => e?.equipment),
    [blocks],
  );
  const muscleGroups = useMemo(
    () => unionFromExercises(blocks, (e) => e?.muscleGroup),
    [blocks],
  );
  const exerciseCount = useMemo(() => countDistinctExercises(blocks), [blocks]);

  const heroBg = useMemo(() => {
    if (!workout) return '';
    return getWorkoutDisplayBannerUrl({
      id: workout.id,
      bannerImageUrl: workout.bannerImageUrl,
    });
  }, [workout]);

  const heroMetaParts: string[] = [];
  if (workout?.estimatedDurationMinutes) {
    heroMetaParts.push(`${workout.estimatedDurationMinutes} min`);
  }
  if (blocks.length) {
    heroMetaParts.push(`${blocks.length} blocks`);
  }
  if (workout?.difficulty) {
    heroMetaParts.push(capitalizeFirst(workout.difficulty));
  }

  const toggleBlock = (blockId: string) => {
    setOpenBlocks((prev) => {
      const next = new Set(prev);
      if (next.has(blockId)) next.delete(blockId);
      else next.add(blockId);
      return next;
    });
  };

  const breadcrumb =
    origin === 'member' && memberId ? (
      <nav className="twd-breadcrumb" aria-label="Breadcrumb">
        <Link
          to="/trainer-dashboard"
          onClick={(e) => {
            e.preventDefault();
            handleBackToMemberManagement();
          }}
        >
          Member Management
        </Link>
        <span className="twd-bc-sep" aria-hidden="true">/</span>
        <Link to={`/trainer-dashboard/member/${memberId}`}>
          {memberDisplayName ?? 'Member'}
        </Link>
        <span className="twd-bc-sep" aria-hidden="true">/</span>
        <span className="twd-bc-current">
          {loading ? 'Loading\u2026' : workout?.name || 'Workout'}
        </span>
      </nav>
    ) : origin === 'plan' && planId ? (
      <nav className="twd-breadcrumb" aria-label="Breadcrumb">
        <Link
          to="/trainer-dashboard"
          onClick={(e) => {
            e.preventDefault();
            handleBackToWorkoutPlansLibrary();
          }}
        >
          Workout plans
        </Link>
        <span className="twd-bc-sep" aria-hidden="true">/</span>
        <Link to={`/trainer-dashboard/workout-plan/${planId}`}>
          {planTitle ?? 'Plan'}
        </Link>
        <span className="twd-bc-sep" aria-hidden="true">/</span>
        <span className="twd-bc-current">
          {loading ? 'Loading\u2026' : workout?.name || 'Workout'}
        </span>
      </nav>
    ) : (
      <nav className="twd-breadcrumb" aria-label="Breadcrumb">
        <Link
          to="/trainer-dashboard"
          onClick={(e) => {
            e.preventDefault();
            handleBackToLibrary();
          }}
        >
          Library
        </Link>
        <span className="twd-bc-sep" aria-hidden="true">/</span>
        <Link
          to="/trainer-dashboard"
          onClick={(e) => {
            e.preventDefault();
            handleBackToLibrary();
          }}
        >
          Workouts
        </Link>
        <span className="twd-bc-sep" aria-hidden="true">/</span>
        <span className="twd-bc-current">
          {loading ? 'Loading\u2026' : workout?.name || 'Workout'}
        </span>
      </nav>
    );

  const setModeInUrl = (mode: 'view' | 'edit') => {
    const next = new URLSearchParams(searchParams);
    if (mode === 'edit') next.set('mode', 'edit');
    else next.delete('mode');
    setSearchParams(next, { replace: true });
  };

  const requestModeChange = (mode: 'view' | 'edit') => {
    if (mode === pageMode) return;
    if (pageMode === 'edit' && editDirty && mode === 'view') {
      setPendingViewAfterDiscard(true);
      setDiscardConfirmOpen(true);
      return;
    }
    setPageMode(mode);
    setModeInUrl(mode);
    if (mode === 'view') setEditDirty(false);
  };

  const confirmDiscardAndLeaveEdit = () => {
    setDiscardConfirmOpen(false);
    setEditDirty(false);
    if (pendingViewAfterDiscard) {
      setPendingViewAfterDiscard(false);
      setPageMode('view');
      setModeInUrl('view');
    }
  };

  const modeToggle = (
    <TrainerModeToggle
      mode={pageMode}
      onView={() => requestModeChange('view')}
      onEdit={() => requestModeChange('edit')}
      editDisabled={!libraryEditEnabled}
      className="twd-mode-toggle"
    />
  );

  const assignWorkoutButton = workout ? (
    <TrainerActionButton
      variant="primary"
      icon={<Plus width={14} height={14} aria-hidden />}
      onClick={() => {
        void assignWorkout.handleAssignWorkout(workout.id);
      }}
      aria-label="Assign workout"
    >
      Assign workout
    </TrainerActionButton>
  ) : null;

  const handleWorkoutSaved = async (result: WorkoutWithBlocks) => {
    setWorkout(result);
    setWorkoutSaveToast(true);
    setPageMode('view');
    setModeInUrl('view');
    setEditDirty(false);
  };

  const handleConfirmDeleteWorkout = async () => {
    if (!workout?.id) return;
    try {
      await WorkoutService.deleteWorkout(workout.id);
      setWorkoutDeleteConfirm(false);
      handleBackToLibrary();
    } catch (err) {
      console.error('Delete workout:', err);
      alert('Failed to delete workout. Please try again.');
    }
  };

  const handleDuplicateFromEdit = async () => {
    if (!workout?.id || duplicateInFlight) return;
    setDuplicateInFlight(true);
    setDuplicateToastOpen(true);
    try {
      const dup = await WorkoutService.duplicateTrainerWorkout(workout.id);
      navigate(`/trainer-dashboard/workout/${dup.new_workout_id}?mode=edit`, {
        state: { memberDisplayName: navHints?.memberDisplayName, planName: navHints?.planName },
      });
    } catch (err) {
      console.error('Duplicate workout:', err);
      setDuplicateToastOpen(false);
      alert(err instanceof Error ? err.message : 'Failed to duplicate workout.');
    } finally {
      setDuplicateInFlight(false);
    }
  };

  /** Reuses the dashboard's primary nav rail (TrainerLeftRail). Section clicks navigate
   *  back to the dashboard with a localStorage hint so the right tab is restored —
   *  same convention `handleBackToLibrary` uses for the breadcrumb links. */
  const handleRailSelect = createTrainerDrillDownSectionSelect(navigate);

  const railActiveSection: 'members' | 'library' | 'chat' | 'control' =
    origin === 'member' ? 'members' : 'library';

  const drawerOpen = drawerExercise !== null;

  const closeExerciseDrawer = () => {
    setDrawerExercise(null);
    setDrawerIsEdit(false);
  };

  const handleExerciseSaveFromEdit = async (data: CreateExerciseData): Promise<Exercise> => {
    const ex = drawerExercise;
    if (!ex || !workoutId) throw new Error('Missing exercise or workout.');
    const isLibrary = ex.createdBy == null || String(ex.createdBy).trim() === '';
    if (isLibrary) {
      await ExerciseService.upsertExerciseOverride(ex.id, data);
    } else {
      await ExerciseService.updateExercise(ex.id, data);
    }
    WorkoutService.invalidateWorkoutCache(workoutId);
    const w = await WorkoutService.getWorkoutById(workoutId);
    if (w) setWorkout(w);
    const merged = await ExerciseService.getExerciseById(ex.id);
    if (!merged) throw new Error('Could not reload exercise.');
    return merged;
  };

  const handleExerciseDiscardEdit = async () => {
    const ex = drawerExercise;
    if (!ex || !workoutId) return;
    const fresh = await ExerciseService.getExerciseById(ex.id);
    if (fresh) {
      setDrawerExercise(fresh);
      setWorkout((prev) =>
        prev ? WorkoutService.patchExerciseIntoWorkout(prev, ex.id, fresh) : prev,
      );
    }
    setDrawerIsEdit(false);
  };

  return (
    <TrainerAppLayout
      activeSection={railActiveSection}
      onSectionSelect={handleRailSelect}
      shellClassName={`twd-page twd-page-with-rail${pageMode === 'edit' ? ' twd-page--edit' : ''}`}
      mainClassName="twd-main"
      mainStyle={drawerOpen ? { pointerEvents: 'none' } : undefined}
    >
        <div className="twd-topbar">
          {breadcrumb}
          <div className="twd-topbar-actions">
            {assignWorkoutButton}
            {modeToggle}
          </div>
        </div>

        {loading && <SkeletonState />}

        {!loading && (errored || !workout) && (
          <NotFoundState backLabel={notFoundBack.label} onBack={notFoundBack.onBack} />
        )}

        {!loading && workout && pageMode === 'edit' && libraryEditEnabled && (
          <TrainerWorkoutDetailEditView
            workout={workout}
            onSaved={handleWorkoutSaved}
            onExitEditMode={() => requestModeChange('view')}
            onRequestDeleteWorkout={() => setWorkoutDeleteConfirm(true)}
            onDuplicateWorkout={() => void handleDuplicateFromEdit()}
            duplicateInFlight={duplicateInFlight}
            onDirtyChange={setEditDirty}
          />
        )}

        {!loading && workout && pageMode === 'view' && (
          <>
            {drawerOpen && drawerExercise
              ? createPortal(
                  <div
                    className="twd-exercise-shared-scrim"
                    role="presentation"
                    onClick={() => {
                      if (drawerIsEdit) exerciseEditRef.current?.requestClose();
                      else closeExerciseDrawer();
                    }}
                  />,
                  document.body,
                )
              : null}
            <section
              className="twd-hero"
              aria-label={`${workout.name} overview`}
            >
              <div
                className="twd-hero-banner"
                style={{ backgroundImage: heroBg ? `url(${heroBg})` : undefined }}
              >
                <div className="twd-hero-overlay">
                  <h1 className="twd-hero-title">{workout.name}</h1>
                  {heroMetaParts.length > 0 && (
                    <div className="twd-hero-meta tnum">
                      {workout.estimatedDurationMinutes != null && (
                        <span><strong>{workout.estimatedDurationMinutes}</strong> min</span>
                      )}
                      {blocks.length > 0 && (
                        <span><strong>{blocks.length}</strong> blocks</span>
                      )}
                      {workout.difficulty && (
                        <span>{capitalizeFirst(workout.difficulty)}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="twd-hero-info">
                {workout.note && workout.note.trim() !== '' && (
                  <div className="twd-info-section">
                    <div className="twd-info-label">
                      <span>Trainer note</span>
                      <span className="twd-info-dash" aria-hidden="true" />
                    </div>
                    <p className="twd-info-body">{workout.note}</p>
                  </div>
                )}
                {equipment.length > 0 && (
                  <div className="twd-info-section">
                    <div className="twd-info-label">
                      <span>Equipment</span>
                      <span className="twd-info-dash" aria-hidden="true" />
                    </div>
                    <div className="twd-chips">
                      {equipment.map((e) => (
                        <span key={`eq-${e}`} className="twd-chip">
                          {e}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {muscleGroups.length > 0 && (
                  <div className="twd-info-section">
                    <div className="twd-info-label">
                      <span>Muscle group</span>
                      <span className="twd-info-dash" aria-hidden="true" />
                    </div>
                    <div className="twd-chips">
                      {muscleGroups.map((m) => (
                        <span key={`mg-${m}`} className="twd-chip twd-chip-muscle">
                          {m}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>

            <header className="twd-section-header">
              <h2 className="twd-section-title">Exercise blocks</h2>
              <span className="twd-section-rollup tnum">
                {blocks.length} blocks {META_SEP} {exerciseCount} exercises
              </span>
            </header>

            <div className="twd-blocks">
              {blocks.map((block, i) => {
                const open = openBlocks.has(block.id);
                const runs = groupExerciseRuns(block);
                return (
                  <article
                    key={block.id}
                    className={`twd-block ${open ? 'twd-block-open' : ''}`}
                  >
                    <BlockHeader
                      index={i}
                      block={block}
                      open={open}
                      onToggle={() => toggleBlock(block.id)}
                    />
                    {open && block.note && block.note.trim() !== '' && (
                      <p className="twd-block-note">{block.note}</p>
                    )}
                    {open && (
                      <div className="twd-block-body">
                        {runs.length === 0 ? (
                          <p className="twd-exercise-meta">No exercises in this block.</p>
                        ) : (
                          runs.map((run) => (
                            <ExerciseRow
                              key={run.key + (run.sets[0]?.id ?? '')}
                              run={run}
                              onOpenDetail={(exercise, ref) => {
                                drawerTriggerRef.current = ref.current;
                                setDrawerExercise(exercise);
                                setDrawerIsEdit(false);
                              }}
                            />
                          ))
                        )}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>

            <ExerciseDetailDrawer
              exercise={drawerExercise}
              open={drawerOpen && !drawerIsEdit}
              onClose={closeExerciseDrawer}
              triggerRef={drawerTriggerRef as React.RefObject<HTMLButtonElement | null>}
              onRequestEdit={() => {
                flushSync(() => setDrawerIsEdit(true));
              }}
              renderScrim={false}
            />

            <ExerciseEditDrawer
              ref={exerciseEditRef}
              exercise={drawerExercise}
              open={drawerOpen && drawerIsEdit}
              muscleOptions={tagOptions.muscle}
              equipmentOptions={tagOptions.equipment}
              onReturnToView={() => setDrawerIsEdit(false)}
              onSave={handleExerciseSaveFromEdit}
              onSaveComplete={(merged) => {
                setDrawerExercise(merged);
                setDrawerIsEdit(false);
                setSavedToastExerciseName(merged.name);
              }}
              onDiscard={handleExerciseDiscardEdit}
              renderScrim={false}
            />

            {savedToastExerciseName ? (
              <ExerciseEditSavedToast
                exerciseName={savedToastExerciseName}
                onDismiss={() => setSavedToastExerciseName(null)}
              />
            ) : null}
          </>
        )}

        {workoutSaveToast && workout ? (
          <ExerciseEditSavedToast
            exerciseName={workout.name}
            variant="workout"
            onDismiss={() => setWorkoutSaveToast(false)}
          />
        ) : null}

        {workoutDeleteConfirm && workout ? (
          <TrainerConfirmDialog
            open
            title="Delete this workout?"
            message={
              <>
                Are you sure you want to delete &ldquo;{workout.name}&rdquo;? This cannot be undone.
              </>
            }
            confirmLabel="Delete"
            onCancel={() => setWorkoutDeleteConfirm(false)}
            onConfirm={() => {
              void handleConfirmDeleteWorkout();
            }}
          />
        ) : null}

        {discardConfirmOpen ? (
          <TrainerConfirmDialog
            open
            title="Discard unsaved changes?"
            message="Your edits to this workout will be lost."
            confirmLabel="Discard"
            onCancel={() => {
              setDiscardConfirmOpen(false);
              setPendingViewAfterDiscard(false);
            }}
            onConfirm={confirmDiscardAndLeaveEdit}
          />
        ) : null}

        {duplicateToastOpen ? (
          <ExerciseEditSavedToast
            exerciseName={workout?.name ?? 'Workout'}
            variant="duplicate"
            onDismiss={() => setDuplicateToastOpen(false)}
          />
        ) : null}

        <AssignWorkoutModal
          open={assignWorkout.showAssignWorkoutModal}
          assigningWorkoutId={assignWorkout.assigningWorkoutId}
          workoutName={workout?.name || 'Loading...'}
          assignMembers={assignWorkout.assignMembers}
          loadingAssignMembers={assignWorkout.loadingAssignMembers}
          selectedMemberId={assignWorkout.selectedAssignWorkoutMemberId}
          onSelectedMemberIdChange={assignWorkout.setSelectedAssignWorkoutMemberId}
          workoutDate={assignWorkout.workoutAssignmentDate}
          onWorkoutDateChange={assignWorkout.setWorkoutAssignmentDate}
          assignmentError={assignWorkout.assignWorkoutError}
          isAssigning={assignWorkout.isAssigningWorkout}
          onClose={assignWorkout.handleCloseAssignWorkoutModal}
          onSubmit={() => {
            void assignWorkout.handleAssignWorkoutSubmit();
          }}
          onAddMember={assignWorkout.handleAddMemberFromEmptyWorkout}
        />

        {assignWorkout.assignWorkoutSuccessToast ? (
          <ExerciseEditSavedToast
            variant="assign-workout"
            exerciseName=""
            workoutName={assignWorkout.assignWorkoutSuccessToast.workoutName}
            memberDisplayName={assignWorkout.assignWorkoutSuccessToast.memberDisplayName}
            onDismiss={() => assignWorkout.setAssignWorkoutSuccessToast(null)}
          />
        ) : null}
    </TrainerAppLayout>
  );
};

export default TrainerWorkoutDetailPage;
